'use client';

import React, { useEffect, useState } from 'react';
import { Button } from '@/components/atoms';
import { Modal, useModalController } from '@/components/molecules/Modal';
import { useDashboard } from '@/modules';
import { useAuth } from '@/modules/auth';
import { useWebSocketContext } from '@/modules/websocket/context/WebSocketContext';
import { generateSessionId } from '@/modules/websocket/utils/websocketUtils';
import { usePageActions } from '@/modules/pages/hooks/usePageActions';
import { usePage } from '@/modules/pages';
import { PendingNewPage } from '@/modules/pages/context/PageContext';

interface CreateAIPageModalProps {
    controller: ReturnType<typeof useModalController>;
    pendingNewPage?: PendingNewPage;
}

export function CreateAIPageModal({ controller, pendingNewPage }: CreateAIPageModalProps) {
    const [prompt, setPrompt] = useState('');

    const { sendMessage } = useWebSocketContext();
    const { state: { currentDashboard, selectedDealer } } = useDashboard();
    const { user } = useAuth();
    const { setGeneratingPage } = usePageActions();

    // Reset on close
    useEffect(() => {
        if (!controller.isOpen) {
            setPrompt('');
        }
    }, [controller.isOpen]);

    const handleGenerate = () => {
        if (!selectedDealer?.dealerRefId || !prompt.trim() || !currentDashboard?.id) return;

        const newSessionId = generateSessionId(Number(selectedDealer.dealerRefId));

        const message = {
            action: 'create-page',
            timestamp: Date.now(),
            prompt: prompt.trim(),
            clientId: Number(selectedDealer.dealerRefId),
            dashboardId: currentDashboard.id,
            sessionId: newSessionId,
            userId: user?.userId,
            publishedToAll: pendingNewPage?.publishedToAll ?? false,
            pageTitle: pendingNewPage?.title,
        };

        sendMessage(JSON.stringify(message));

        // Transition to generating state and close modal
        setGeneratingPage({ sessionId: newSessionId, prompt: prompt.trim() });
        controller.close();
    };

    return (
        <Modal
            title="Define Your Objectives"
            bodyClassName="max-w-lg"
            actionButtons={[
                <Button key="cancel" variant="light" onClick={controller.close}>
                    Cancel
                </Button>,
                <Button
                    key="generate"
                    variant="secondary"
                    disabled={!prompt.trim() || !selectedDealer?.dealerRefId}
                    onClick={handleGenerate}
                >
                    Generate Dashboard
                </Button>,
            ]}
            controller={controller}
        >
            <div className="space-y-4">
                <p className="text-sm text-[#5a717a]">
                    Enter your marketing goals and objectives to guide the AI in generating a dashboard that meets your needs.
                </p>
                <textarea
                    className="w-full min-h-[100px] p-3 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#1f8894] focus:border-transparent"
                    placeholder="e.g., Increase ad spend by 20%, improve lead conversion rate, etc."
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.metaKey && prompt.trim()) {
                            handleGenerate();
                        }
                    }}
                />
                <p className="text-xs text-gray-400">Cmd+Enter to submit</p>
            </div>
        </Modal>
    );
}
